/**
 * ErrorContext: global error toast state for the app.
 * ErrorProvider (mounted in App.tsx) holds the current error message and
 * renders an ErrorToast overlay when one is set; the useError hook exposes
 * showError/hideError to pages and components (forms, submission and bounty
 * pages, etc.) for surfacing API and validation errors.
 */
import React, { createContext, useContext, useState, ReactNode } from "react";
import ErrorToast from "@/components/ErrorToast";

interface ErrorContextType {
    showError: (message: string) => void;
    hideError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const useError = () => {
    const context = useContext(ErrorContext);
    if (!context) {
        throw new Error("useError must be used within an ErrorProvider");
    }
    return context;
};

interface ErrorProviderProps {
    children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const showError = (message: string) => {
        setErrorMessage(message);
    };

    const hideError = () => {
        setErrorMessage(null);
    };

    return (
        <ErrorContext.Provider value={{ showError, hideError }}>
            {children}
            {errorMessage && <ErrorToast message={errorMessage} onClose={hideError} />}
        </ErrorContext.Provider>
    );
};
